import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { customerApiService, UserProfileResponse, EmergencyContactRequest, KYCRequest } from 'services/api';

interface UserProfileData {
  profile: UserProfileResponse | null;
  loading: boolean;
  error: string | null;
}

export const useUserProfile = () => {
  const { user, company } = useAuth();
  const [userProfileData, setUserProfileData] = useState<UserProfileData>({
    profile: null,
    loading: true,
    error: null
  });

  const fetchUserProfile = async () => {
    if (!user || !company) {
      setUserProfileData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setUserProfileData(prev => ({ ...prev, loading: true, error: null }));

      const response = await customerApiService.getCurrentUser();

      if (response.success && response.data) {
        setUserProfileData({
          profile: response.data,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.message || 'Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfileData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user profile'
      }));
    }
  };

  const updateUserProfile = async (updates: Partial<UserProfileResponse>) => {
    try {
      const response = await customerApiService.updateUserProfile(updates);

      if (response.success) {
        await fetchUserProfile(); // Refresh the data
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update user profile');
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const updateEmergencyContact = async (contactData: EmergencyContactRequest) => {
    try {
      const response = await customerApiService.updateEmergencyContact(contactData);

      if (response.success) {
        await fetchUserProfile(); // Refresh the data
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update emergency contact');
      }
    } catch (error) {
      console.error('Error updating emergency contact:', error);
      throw error;
    }
  };

  const submitKYC = async (kycData: KYCRequest) => {
    try {
      const response = await customerApiService.submitKYC(kycData);

      if (response.success) {
        await fetchUserProfile(); // Refresh the data
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to submit KYC data');
      }
    } catch (error) {
      console.error('Error submitting KYC:', error);
      throw error;
    }
  };

  const refreshData = () => {
    fetchUserProfile();
  };

  useEffect(() => {
    fetchUserProfile();
  }, [user, company]);

  return {
    ...userProfileData,
    updateUserProfile,
    updateEmergencyContact,
    submitKYC,
    refreshData
  };
};
